import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// ----- Sliding-window token refresh ----------------------------------------
// Strategy: on every outgoing request, decode the JWT's `exp` claim. When
// the token is within REFRESH_WINDOW_SECONDS of expiring, fire a refresh
// request *before* the original request goes out. Concurrent refreshes are
// deduped via a singleflight promise. Once the backend's `SESSION_MAX_HOURS`
// hard cap is hit, the refresh endpoint returns 401 → we fall through to
// the normal 401 handler below which boots the user to /login.
const REFRESH_WINDOW_SECONDS = 2 * 3600;  // refresh when ≤2h to expiry

let refreshPromise = null;

const tokenExpSeconds = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp;
  } catch {
    return 0;
  }
};

const refreshAccessToken = async () => {
  // Singleflight: if a refresh is already in flight, every caller awaits it.
  if (refreshPromise) return refreshPromise;
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  refreshPromise = axios
    .post(`${API_URL}/api/auth/refresh`, null, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      const newToken = res.data?.data?.access_token;
      if (newToken) localStorage.setItem('access_token', newToken);
      return newToken;
    })
    .catch(() => null)
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

api.interceptors.request.use(async (config) => {
  // Skip refresh logic on the refresh + login endpoints themselves to avoid
  // recursion / infinite loops.
  const url = config.url || '';
  const isAuthFlow = url.includes('/auth/refresh') || url.includes('/auth/login');

  let token = localStorage.getItem('access_token');
  if (token && !isAuthFlow) {
    const exp = tokenExpSeconds(token);
    const now = Math.floor(Date.now() / 1000);
    if (exp && exp - now < REFRESH_WINDOW_SECONDS) {
      // Token expiring soon — refresh first, then use the new one.
      const fresh = await refreshAccessToken();
      if (fresh) token = fresh;
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || '';
    const isMigrationStatus = url.includes('/migration/status');
    const isAuthFlow = url.includes('/auth/login') || url.includes('/auth/refresh');

    // On a 401 (other than auth flow / migration poller) try ONE refresh —
    // covers the edge where the token expired between the request-interceptor
    // refresh-window check and the server actually receiving the call.
    if (error.response?.status === 401 && !isAuthFlow && !isMigrationStatus
        && !error.config?._retried_after_refresh) {
      const fresh = await refreshAccessToken();
      if (fresh) {
        error.config._retried_after_refresh = true;
        error.config.headers.Authorization = `Bearer ${fresh}`;
        return api.request(error.config);
      }
      // Refresh failed → session is over. Wipe storage and bounce.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    } else if (error.response?.status === 401 && !isAuthFlow && !isMigrationStatus) {
      // Second 401 even after a refresh attempt — give up.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
